import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '@/services/auth';
import {
  SESSION_EXPIRED_EVENT,
  accessTokenExpiringSoon,
  expireSession,
  refreshSession,
} from '@/services/session';
import { User } from '@/types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          // A tab reopened after the access token expired: renew it now, or
          // go straight to sign-in instead of loading pages that will 401.
          if (accessTokenExpiringSoon() && !(await refreshSession())) {
            expireSession();
            return;
          }
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // The API client fires this when a session can't be renewed.
    const onExpired = () => setUser(null);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const login = async (email: string, password: string) => {
    await authService.login(email, password);

    // Check if user has admin role
    if (!authService.hasRole('admin') && !authService.hasRole('pharmacist')) {
      authService.logout();
      throw new Error('You do not have permission to access the admin portal.');
    }

    setUser(authService.getCurrentUser());
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const hasRole = (role: string): boolean => {
    return authService.hasRole(role);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
